import {
  Button,
  type ButtonProps,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { t } from "@lingui/macro";
import { SortOrder } from "@src/__generated__/urql-graphql";
import { WrapComponent } from "@src/utils/components/WrapComponent";
import { cloneElement, type FC, useCallback } from "react";
import { Icon } from "../Icon";

type Props<T> = {
  title: string;
  tooltip?: string;
  order: SortOrder | undefined;
  column: T;
  onOrderChange: ({
    order,
    column,
  }: {
    order: SortOrder | undefined;
    column: T;
  }) => void;
  preventSortCancel?: boolean;
} & ButtonProps;

const SortIcon: FC<{ order: SortOrder | undefined }> = ({ order }) => {
  const NoSortIcon = <Icon name="chevron-selector-vertical" />;
  const AscIcon = <Icon name="chevron-up" />;
  const DescIcon = <Icon name="chevron-down" />;

  const getSortIcon = useCallback(
    (order: SortOrder | undefined) => {
      if (!order) return NoSortIcon;
      if (order === SortOrder.Asc) return AscIcon;
      return DescIcon;
    },
    [order],
  );

  return cloneElement(getSortIcon(order), { ml: 1 });
};

export const SortTitle = <T,>({
  onOrderChange,
  order,
  column,
  title,
  tooltip,
  preventSortCancel,
  ...props
}: Props<T>) => {
  return (
    <WrapComponent
      if={!!tooltip}
      with={(children) => <Tooltip label={tooltip}>{children}</Tooltip>}
    >
      <Button
        alignItems="center"
        minW="unset"
        h="unset"
        minH="unset"
        color={order ? "blue.500" : "inherit"}
        fontSize="inherit"
        fontWeight="inherit"
        _hover={{
          color: "blue.500",
        }}
        role="group"
        variant="unstyled"
        {...props}
        onClick={(e) => {
          e.stopPropagation();
          onOrderChange({
            column,
            order: order === SortOrder.Desc ? SortOrder.Asc : SortOrder.Desc,
          });
        }}
      >
        {title}
        {!!tooltip && <Icon name="info-circle" ml="1" />}
        <SortIcon order={order} />
        {!!order && !preventSortCancel && (
          <IconButton
            w="0"
            minW="unset"
            h="unset"
            opacity="0"
            _groupHover={{
              opacity: "1",
              pointerEvents: "unset",
              p: "1",
              w: "unset",
            }}
            pointerEvents="none"
            transition="all"
            aria-label={t`Sort delete button`}
            colorScheme="grey"
            icon={<Icon name="x-close" />}
            onClick={(e) => {
              e.stopPropagation();
              onOrderChange({
                column,
                order: undefined,
              });
            }}
            transitionDuration="200ms"
            variant="ghost"
          />
        )}
      </Button>
    </WrapComponent>
  );
};
